import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { safeFormat } from '../utils/dateUtils';

const Appointments = () => {
  const { t } = useTranslation();
  const { isAdmin, isBarber } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    client_phone: '',
    barber_id: '',
    date: new Date().toISOString().slice(0, 16),
    status: 'scheduled'
  });

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [appointmentsRes, barbersRes] = await Promise.all([api.get('/appointments'), api.get('/barbers')]);
      setAppointments(appointmentsRes.data);
      setBarbers(barbersRes.data);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, date: new Date(formData.date).toISOString() };
      await api.post('/appointments', payload);
      toast.success('تم إضافة الموعد بنجاح');
      setOpen(false);
      setFormData({ client_name: '', client_phone: '', barber_id: '', date: new Date().toISOString().slice(0, 16), status: 'scheduled' });
      fetchData();
    } catch (error) {
      toast.error('فشلت العملية');
    }
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الموعد؟')) return;
    try {
      await api.delete(`/appointments/${appointmentId}`);
      toast.success('تم حذف الموعد بنجاح');
      fetchData();
    } catch (error) {
      toast.error('فشل حذف الموعد');
    }
  };

  const canAddOrEdit = isAdmin() || isBarber();

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('appointments')}</h1>
          <p className="text-muted-foreground">إدارة المواعيد</p>
        </div>
        {canAddOrEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-appointment-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 me-2" />إضافة موعد</Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10">
              <DialogHeader><DialogTitle>إضافة موعد</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">اسم الزبون</label>
                  <Input data-testid="appointment-client-name-input" value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} required className="bg-secondary/50 border-white/10" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">رقم الهاتف</label>
                  <Input data-testid="appointment-client-phone-input" value={formData.client_phone} onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })} className="bg-secondary/50 border-white/10" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">الحلاق</label>
                  <Select value={formData.barber_id} onValueChange={(value) => setFormData({ ...formData, barber_id: value })}>
                    <SelectTrigger data-testid="appointment-barber-select" className="bg-secondary/50 border-white/10"><SelectValue placeholder="اختر الحلاق" /></SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      {barbers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">التاريخ والوقت</label>
                  <Input data-testid="appointment-date-input" type="datetime-local" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="bg-secondary/50 border-white/10" />
                </div>
                <Button data-testid="submit-appointment-btn" type="submit" className="w-full bg-primary text-primary-foreground">{t('save')}</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50 border-b border-white/5">
              <tr>
                <th className="px-6 py-4 text-start text-sm font-semibold">التاريخ والوقت</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الزبون</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الهاتف</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الحلاق</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('status')}</th>
                {canAddOrEdit && <th className="px-6 py-4 text-start text-sm font-semibold">{t('actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {appointments.map((apt) => {
                const barber = barbers.find(b => b.id === apt.barber_id);
                return (
                  <tr key={apt.id} className="border-b border-white/5 hover:bg-secondary/20">
                    <td className="px-6 py-4">{safeFormat(apt.date, 'yyyy-MM-dd HH:mm', { fallback: '-' })}</td>
                    <td className="px-6 py-4">{apt.client_name}</td>
                    <td className="px-6 py-4 text-muted-foreground">{apt.client_phone || '-'}</td>
                    <td className="px-6 py-4">{barber?.name || 'N/A'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        apt.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        apt.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {apt.status === 'completed' ? 'مكتمل' : apt.status === 'scheduled' ? 'مجدول' : 'ملغي'}
                      </span>
                    </td>
                    {canAddOrEdit && (
                      <td className="px-6 py-4">
                        <Button data-testid={`delete-appointment-${apt.id}`} onClick={() => handleDelete(apt.id)} variant="ghost" size="sm" className="text-red-500 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Appointments;